import { COMMON_SPACING_STEPS, zIndices as defaultZIndices, spacingValue, } from '@smoothie-framework/ui-core';
import { toCssVarRef, tokenPathToCssVar } from '@smoothie-framework/ui-core/resolve';
import { TEXT_STYLE_PROPERTY_NAMES } from '@smoothie-framework/ui-core/theme';
export function toCssVariables(theme, options = {}) {
    const resolvedOptions = typeof options === 'string'
        ? { prefix: options, includeTextStyles: true }
        : { prefix: options.prefix ?? '--smth', includeTextStyles: options.includeTextStyles ?? true };
    const prefix = resolvedOptions.prefix;
    const vars = {};
    const setVar = (path, value) => {
        vars[tokenPathToCssVar(path, prefix)] = toCssVarRef(value, prefix);
    };
    const resolveTextStylePropertyValue = (propertyName, rawValue) => {
        if (typeof rawValue === 'number') {
            return String(rawValue);
        }
        if (propertyName === 'fontFamily' && Object.prototype.hasOwnProperty.call(theme.typography.fontFamily, rawValue)) {
            return `var(${tokenPathToCssVar(`typography.fontFamily.${rawValue}`, prefix)})`;
        }
        if (propertyName === 'fontSize' && Object.prototype.hasOwnProperty.call(theme.typography.fontSize, rawValue)) {
            return `var(${tokenPathToCssVar(`typography.fontSize.${rawValue}`, prefix)})`;
        }
        if (propertyName === 'fontWeight' && Object.prototype.hasOwnProperty.call(theme.typography.fontWeight, rawValue)) {
            return `var(${tokenPathToCssVar(`typography.fontWeight.${rawValue}`, prefix)})`;
        }
        if (propertyName === 'lineHeight' && Object.prototype.hasOwnProperty.call(theme.typography.lineHeight, rawValue)) {
            return `var(${tokenPathToCssVar(`typography.lineHeight.${rawValue}`, prefix)})`;
        }
        if (propertyName === 'letterSpacing' && Object.prototype.hasOwnProperty.call(theme.typography.letterSpacing, rawValue)) {
            return `var(${tokenPathToCssVar(`typography.letterSpacing.${rawValue}`, prefix)})`;
        }
        return toCssVarRef(rawValue, prefix);
    };
    for (const [colorName, shades] of Object.entries(theme.colors)) {
        if (typeof shades === 'string') {
            setVar(`colors.${colorName}`, shades);
        }
        else {
            for (const [shade, value] of Object.entries(shades)) {
                setVar(`colors.${colorName}.${shade}`, value);
            }
        }
    }
    for (const step of COMMON_SPACING_STEPS) {
        setVar(`spacing.${String(step)}`, spacingValue(step));
    }
    for (const [key, value] of Object.entries(theme.sizes ?? {})) {
        setVar(`sizes.${key}`, value);
    }
    for (const [family, value] of Object.entries(theme.typography.fontFamily)) {
        setVar(`typography.fontFamily.${family}`, value);
    }
    for (const [size, value] of Object.entries(theme.typography.fontSize)) {
        setVar(`typography.fontSize.${size}`, `${value}rem`);
    }
    for (const [lineHeight, value] of Object.entries(theme.typography.lineHeight)) {
        setVar(`typography.lineHeight.${lineHeight}`, String(value));
    }
    for (const [weight, value] of Object.entries(theme.typography.fontWeight)) {
        setVar(`typography.fontWeight.${weight}`, String(value));
    }
    for (const [letterSpacing, value] of Object.entries(theme.typography.letterSpacing)) {
        setVar(`typography.letterSpacing.${letterSpacing}`, value);
    }
    for (const [key, value] of Object.entries(theme.borders ?? {})) {
        setVar(`borders.${key}`, value);
    }
    for (const [key, value] of Object.entries(theme.borderWidths ?? {})) {
        setVar(`borderWidths.${key}`, value);
    }
    for (const [key, value] of Object.entries(theme.borderStyles ?? {})) {
        setVar(`borderStyles.${key}`, value);
    }
    for (const [key, value] of Object.entries(theme.radii)) {
        setVar(`radii.${key}`, typeof value === 'number' ? `${value}px` : value);
    }
    for (const [key, value] of Object.entries(theme.shadows)) {
        setVar(`shadows.${key}`, value);
    }
    for (const [key, value] of Object.entries(theme.zIndices ?? defaultZIndices)) {
        setVar(`zIndices.${key}`, String(value));
    }
    for (const [tokenPath, tokenValue] of Object.entries(theme.tokens ?? {})) {
        setVar(tokenPath, tokenValue);
    }
    if (resolvedOptions.includeTextStyles) {
        // Responsive component-token overrides and textStyle breakpoint overrides are
        // emitted by compileThemeCss into component-variables.css rather than this
        // flat variable snapshot.
        for (const [styleName, definition] of Object.entries(theme.textStyles ?? {})) {
            const baseProperties = definition.base;
            if (!baseProperties) {
                continue;
            }
            for (const propertyName of TEXT_STYLE_PROPERTY_NAMES) {
                const rawValue = baseProperties[propertyName];
                if (rawValue === undefined) {
                    continue;
                }
                vars[tokenPathToCssVar(`textStyles.${styleName}.${propertyName}`, prefix)] =
                    resolveTextStylePropertyValue(propertyName, rawValue);
            }
        }
    }
    return vars;
}
export function getCssVariable(path, prefix = '--smth') {
    const normalizedPath = path.replace(/^\{(.+)\}$/, '$1');
    return `var(${tokenPathToCssVar(normalizedPath, prefix)})`;
}
export function toCssStylesheet(theme, options = {}) {
    const vars = toCssVariables(theme, options);
    const lines = Object.entries(vars).map(([key, value]) => `  ${key}: ${value};`);
    return `:root {\n${lines.join('\n')}\n}`;
}
//# sourceMappingURL=variables.js.map